import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { Button, ThemedText, ConfirmDialog } from '../components';
import labReportTemplateService from '../api/services/labReportTemplateService';
import { extractItems } from '../utils/responseUtils';
import { PageContainer } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { FormGroup, FormLabel, StyledSelect as Select } from '../components/FormStyles';
import { Plus, Edit2, Trash2, FlaskConical, Star } from 'lucide-react';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
`;

const StyledInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  width: 100%;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const StyledTextarea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  width: 100%;
  min-height: 100px;
  resize: vertical;
  font-family: monospace;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.muted};
  gap: ${props => props.theme.spacing.md};
`;

const DefaultBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  background-color: #fef3c7;
  color: #92400e;
`;

export const LabReportTemplatesPage = () => {
  const theme = useTheme();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    labVendor: '',
    structureHintsJson: '',
    extractionConfigJson: '',
    isDefault: false,
  });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await labReportTemplateService.getAll();
      setTemplates(extractItems(response) || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openCreate = () => {
    setEditingTemplate(null);
    setForm({ name: '', description: '', labVendor: '', structureHintsJson: '', extractionConfigJson: '', isDefault: false });
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditingTemplate(t);
    setForm({
      name: t.name || '',
      description: t.description || '',
      labVendor: t.labVendor || '',
      structureHintsJson: t.structureHintsJson || '',
      extractionConfigJson: t.extractionConfigJson || '',
      isDefault: t.isDefault || false,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    // Validate JSON fields
    if (form.structureHintsJson) {
      try { JSON.parse(form.structureHintsJson); } catch {
        toast.error('Structure Hints must be valid JSON');
        return;
      }
    }
    if (form.extractionConfigJson) {
      try { JSON.parse(form.extractionConfigJson); } catch {
        toast.error('Extraction Config must be valid JSON');
        return;
      }
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await labReportTemplateService.update(editingTemplate.id, form);
        toast.success('Template updated');
      } else {
        await labReportTemplateService.create(form);
        toast.success('Template created');
      }
      setShowModal(false);
      fetchTemplates();
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await labReportTemplateService.delete(deleteId);
      toast.success('Template deleted');
      setDeleteId(null);
      fetchTemplates();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  return (
    <Layout>
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <ThemedText $size="h2" $weight="bold">Lab Report Templates</ThemedText>
          <Button onClick={openCreate}>
            <Plus size={16} style={{ marginRight: 6 }} /> New Template
          </Button>
        </PageHeader>

        {templates.length === 0 && !loading ? (
          <EmptyState theme={theme}>
            <FlaskConical size={48} />
            <ThemedText $size="lg">No templates yet</ThemedText>
            <ThemedText $color="muted">Create a template to improve AI extraction accuracy for known lab formats</ThemedText>
          </EmptyState>
        ) : (
          <TableContainer theme={theme}>
            <Table>
              <Thead theme={theme}>
                <tr>
                  <Th theme={theme}>Name</Th>
                  <Th theme={theme}>Lab Vendor</Th>
                  <Th theme={theme}>Description</Th>
                  <Th theme={theme}>Default</Th>
                  <Th theme={theme}>Actions</Th>
                </tr>
              </Thead>
              <Tbody>
                {templates.map(t => (
                  <Tr key={t.id} theme={theme} onClick={() => openEdit(t)}>
                    <Td theme={theme}><strong>{t.name}</strong></Td>
                    <Td theme={theme}>{t.labVendor || '-'}</Td>
                    <Td theme={theme}>{t.description || '-'}</Td>
                    <Td theme={theme}>
                      {t.isDefault && (
                        <DefaultBadge>
                          <Star size={10} /> Default
                        </DefaultBadge>
                      )}
                    </Td>
                    <Td theme={theme} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button $variant="ghost" $size="small" onClick={() => openEdit(t)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button $variant="ghost" $size="small" onClick={() => setDeleteId(t.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}

        {/* Create/Edit Modal */}
        <ModalOverlay $isOpen={showModal} onClick={() => setShowModal(false)}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <ModalHeader theme={theme}>
              {editingTemplate ? 'Edit Template' : 'New Template'}
            </ModalHeader>
            <ModalBody theme={theme}>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Name *</FormLabel>
                <StyledInput theme={theme} value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Element Materials Technology" />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Lab Vendor</FormLabel>
                <StyledInput theme={theme} value={form.labVendor}
                  onChange={e => setForm(f => ({ ...f, labVendor: e.target.value }))}
                  placeholder="e.g. Element, ALS, SGS" />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Description</FormLabel>
                <StyledInput theme={theme} value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description of this lab format" />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Structure Hints (JSON)</FormLabel>
                <StyledTextarea theme={theme} value={form.structureHintsJson}
                  onChange={e => setForm(f => ({ ...f, structureHintsJson: e.target.value }))}
                  placeholder='{"expectedSections": ["Microbiological Analysis"], "reportNumberField": "Report Number"}' />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Extraction Config (JSON)</FormLabel>
                <StyledTextarea theme={theme} value={form.extractionConfigJson}
                  onChange={e => setForm(f => ({ ...f, extractionConfigJson: e.target.value }))}
                  placeholder='{"skipPages": [1], "dateFormat": "MMM dd, yyyy"}' />
              </FormGroup>
              <FormGroup theme={theme} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={form.isDefault}
                  onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} />
                <FormLabel theme={theme} style={{ marginBottom: 0 }}>Set as default template</FormLabel>
              </FormGroup>
            </ModalBody>
            <ModalFooter theme={theme}>
              <Button $variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>

        <ConfirmDialog
          isOpen={!!deleteId}
          title="Delete Template"
          message="Are you sure you want to delete this template? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      </PageContainer>
    </Layout>
  );
};
